/* RESPONSIBLE TEAM: team-proactive-support */
import { debounce } from '@ember/runloop';
import UserAgentDetector from './user-agent-detector';
import ENV from 'embercom/config/environment';

enum GridWheelEvent {
  MouseWheelXPan,
  MouseWheelYPan,
  MouseWheelZoom,
  TrackpadPan,
  TrackpadZoom,
}

interface FullWheelEvent extends WheelEvent {
  wheelDelta: number;
}

export { FullWheelEvent, GridWheelEvent };

export default class WheelEventDecoder {
  userAgentDetector: UserAgentDetector = new UserAgentDetector();

  // To prevent an edge case where sometimes a trackpad pan event can look like
  // a mouse wheel zoom event, we mark that we recently panned with the trackpad
  // and prevent mouse zooming for a short time.
  recentlyUsedTrackpadScroll = false;

  decode(event: FullWheelEvent, useMouseWheelForZoom: boolean): GridWheelEvent {
    if (this.userAgentDetector.isFirefox) {
      return this.decodeForFirefox(event, useMouseWheelForZoom);
    } else {
      return this.decodeForDefault(event, useMouseWheelForZoom);
    }
  }

  /*
   * In Firefox, the deltaMode of the event is always set to 1 when
   * using a mouse wheel.
   */
  private decodeForFirefox(event: FullWheelEvent, useMouseWheelForZoom: boolean): GridWheelEvent {
    let isMouseWheelEvent = event.deltaMode === 1;

    if (event.ctrlKey || event.metaKey) {
      return GridWheelEvent.TrackpadZoom;
    } else if (isMouseWheelEvent) {
      return this.decodeMouseWheelEvent(useMouseWheelForZoom, event.shiftKey);
    }

    return GridWheelEvent.TrackpadPan;
  }

  /*
   * In Chrome, the deltaY of the mouse event is always a whole number when
   * using a mouse wheel.
   */
  private decodeForDefault(event: FullWheelEvent, useMouseWheelForZoom: boolean): GridWheelEvent {
    let isMouseWheelEvent =
      (event.deltaX === 0 || event.deltaY === 0) && event.wheelDelta % 120 === 0;

    if (event.ctrlKey || event.metaKey) {
      return GridWheelEvent.TrackpadZoom;
    } else if (!this.recentlyUsedTrackpadScroll && isMouseWheelEvent) {
      return this.decodeMouseWheelEvent(useMouseWheelForZoom, event.shiftKey);
    }

    this.recentlyUsedTrackpadScroll = true;
    debounce(this, this.unsetRecentTrackpadScroll, ENV.APP._10000MS);
    return GridWheelEvent.TrackpadPan;
  }

  private decodeMouseWheelEvent(
    useMouseWheelForZoom: boolean,
    isShiftKeyPressed: boolean,
  ): GridWheelEvent {
    if (isShiftKeyPressed) {
      return GridWheelEvent.MouseWheelXPan;
    } else {
      return useMouseWheelForZoom ? GridWheelEvent.MouseWheelZoom : GridWheelEvent.MouseWheelYPan;
    }
  }

  private unsetRecentTrackpadScroll() {
    this.recentlyUsedTrackpadScroll = false;
  }
}
